import * as React from 'react';
import { FiscalSystem, GetDeviceConfigurationResponse, Res } from 'core/api';
import { differenceInDays, differenceInHours } from 'date-fns';
import * as Images from '../Images';
import { DialogBody } from 'core/ui/controls/Dialog';
import { Modal } from 'core/ui-next/modal';
import { Box, Button, VStack, Text, HStack } from '@chakra-ui/react';

const MAX_SECONDS = 10;
const HOURS_BETWEEN_NOTIFICATIONS_FOR_LESS_THAN_30_DAYS = 1;
const HOURS_BETWEEN_NOTIFICATIONS_FOR_LESS_THAN_60_DAYS = 24;

export type Status = {
	fiscalSystem: Exclude<FiscalSystem, FiscalSystem.None>,
	lastNotification: Nullable<Date>;
	remainingDays: number,
	remainingHours: number,
	expirationDate: Date,
}

let status: Nullable<Status> = null;

const NotificationContent = (props: {
	type: 'info' | 'warning' | 'danger'
	title: string,
	icon?: SvgIcon,
	secondaryTitle?: string,
	paragraphs: string[],
	remainingSecondsText?: string,
	onAccept?: () => void
}) => {

	const background: Record<string, string> = {
		'info': 'primary',
		'warning': 'warning',
		'danger': 'error'
	};

	const { type, title, secondaryTitle, icon, paragraphs, onAccept, remainingSecondsText } = props;

	const Icon = icon;

	return <VStack bg={{ base: 'white', _dark: 'neutral.900' }}>
		<HStack bg={`${background[type]}`} h='12' justifyContent={'center'} padding='2' w={'full'} mb={6}>
			<Text fontWeight='bold' fontSize='md' color='white'>{title}</Text>
		</HStack>

		{Icon ? <HStack justifyContent={'center'}>{<Icon fill={`${background[type]}`} height={42} width={42} />}</HStack> : null}
		{secondaryTitle ? <HStack justifyContent={'center'}>{<Text fontWeight='bold' fontSize='md'>{secondaryTitle}</Text>}</HStack> : null}

		<VStack justifyContent={'center'} p={6} width={'full'}>
			{paragraphs.map((text, index) => <Text key={index}>{text}</Text>)}
		</VStack>

		{
			onAccept
				? <HStack justifyContent={'center'}>
					<Button bg={`${background[type]}`} color='white' onClick={onAccept}>{Res.Common.UNDERSTOOD_BTN()}</Button>
				</HStack>
				: <HStack style={{ height: 34 }}></HStack>
		}

		<Box width={'full'} px={6} py={3}>{remainingSecondsText ? <Text textAlign='right'>{remainingSecondsText}</Text> : <Text>&nbsp;</Text>}</Box>
	</VStack >;
};

const SixtyDaysWarning = ({ expirationDate, onAccept, certificateType: mode }: { expirationDate: Date, onAccept: () => void, certificateType: string }) =>
	<NotificationContent
		type='info'
		title={Res.Web.CERTIFICATE_NOTIFICATION_SIXTY_DAYS_MAIN_TITLE(mode)}
		secondaryTitle={Res.Web.CERTIFICATE_NOTIFICATION_SIXTY_DAYS_SECONDARY_TITLE(expirationDate)}
		paragraphs={[Res.Web.CERTIFICATE_NOTIFICATION_SIXTY_DAYS_INFO_LBL1(mode, expirationDate), Res.Web.CERTIFICATE_NOTIFICATION_SIXTY_DAYS_INFO_LBL2()]}
		onAccept={onAccept} />;

const ThirtyDaysWarning = ({ remainingDays, onAccept, certificateType: mode }: { remainingDays: number, onAccept: () => void, certificateType: string }) =>
	<NotificationContent
		type='warning'
		title={Res.Web.CERTIFICATE_NOTIFICATION_THIRTY_DAYS_MAIN_TITLE(mode)}
		secondaryTitle={Res.Web.CERTIFICATE_NOTIFICATION_THIRTY_DAYS_SECONDARY_TITLE(remainingDays)}
		paragraphs={[Res.Web.CERTIFICATE_NOTIFICATION_THIRTY_DAYS_INFO_LBL1(mode, remainingDays), Res.Web.CERTIFICATE_NOTIFICATION_THIRTY_DAYS_INFO_LBL2()]}
		onAccept={onAccept} />;

const FifteenDaysWarning = ({ remainingDays, remainingSeconds, onAccept, certificateType: mode }: { remainingDays: number, remainingSeconds: number, onAccept: () => void, certificateType: string }) =>
	<NotificationContent
		type='danger'
		title={Res.Web.CERTIFICATE_NOTIFICATION_FIFTEEN_DAYS_MAIN_TITLE(mode)}
		secondaryTitle={Res.Web.CERTIFICATE_NOTIFICATION_FIFTEEN_DAYS_SECONDARY_TITLE(remainingDays)}
		paragraphs={[Res.Web.CERTIFICATE_NOTIFICATION_FIFTEEN_DAYS_INFO_LBL1(remainingDays, mode), Res.Web.CERTIFICATE_NOTIFICATION_FIFTEEN_DAYS_INFO_LBL2()]}
		onAccept={remainingSeconds === 0 ? onAccept : undefined}
		remainingSecondsText={Res.Web.CERTIFICATE_NOTIFICATION_REMAINING_SECONDS_LBL(remainingSeconds)}
	/>;

const HoursWarning = ({ remainingHours, remainingSeconds, onAccept, certificateType: mode }: { remainingHours: number, remainingSeconds: number, onAccept: () => void, certificateType: string }) =>
	<NotificationContent
		type='danger'
		title={Res.Web.CERTIFICATE_NOTIFICATION_HOURS_MAIN_TITLE(mode)}
		secondaryTitle={Res.Web.CERTIFICATE_NOTIFICATION_HOURS_SECONDARY_TITLE(remainingHours)}
		paragraphs={[Res.Web.CERTIFICATE_NOTIFICATION_HOURS_INFO_LBL1(remainingHours, mode), Res.Web.CERTIFICATE_NOTIFICATION_HOURS_INFO_LBL2()]}
		onAccept={remainingSeconds === 0 ? onAccept : undefined}
		remainingSecondsText={Res.Web.CERTIFICATE_NOTIFICATION_REMAINING_SECONDS_LBL(remainingSeconds)}
	/>;

const ExpiredWarning = ({ remainingSeconds, onAccept, certificateType: mode }: { remainingSeconds: number, onAccept: () => void, certificateType: string }) =>
	<NotificationContent
		type='danger'
		title={Res.Web.CERTIFICATE_NOTIFICATION_EXPIRED_MAIN_TITLE(mode)}
		icon={Images.Cancel}
		// Mientras que TicketBAI no puede emitir facturas, Verifactu sí puede, por lo que los textos cambian y no se pueden parametrizar.
		paragraphs={[mode === 'VERI*FACTU' ? Res.Web.CERTIFICATE_NOTIFICATION_EXPIRATION_INFO_VERIFACTU_LBL1() : Res.Web.CERTIFICATE_NOTIFICATION_EXPIRATION_INFO_TICKET_BAI_LBL1()]}
		onAccept={remainingSeconds === 0 ? onAccept : undefined}
		remainingSecondsText={Res.Web.CERTIFICATE_NOTIFICATION_REMAINING_SECONDS_LBL(remainingSeconds)}
	/>;


const NotificationModal: React.FC<Status & Modal.Actions<void>> = ({
	remainingDays,
	remainingHours,
	expirationDate,
	fiscalSystem,
	close
}) => {
	const [remainingSeconds, setRemainingSeconds] = React.useState(MAX_SECONDS);

	React.useEffect(() => {
		const startMs = Date.now();

		const timerId = setInterval(() => {
			const seconds = Math.max(MAX_SECONDS - Math.floor((Date.now() - startMs) / 1000), 0);
			setRemainingSeconds(seconds);

			if (seconds === 0) {
				clearInterval(timerId);
			}
		}, 1000);

		return () => clearInterval(timerId);
	}, []);

	const Notification = () => {
		const certificateType = fiscalSystem === FiscalSystem.Verifactu ? 'VERI*FACTU' : 'TicketBAI';

		if (remainingDays <= 60 && remainingDays > 30)
			return <SixtyDaysWarning expirationDate={expirationDate} onAccept={close} certificateType={certificateType} />;
		if (remainingDays <= 30 && remainingDays > 15)
			return <ThirtyDaysWarning remainingDays={remainingDays} onAccept={close} certificateType={certificateType} />;
		if (remainingDays <= 15 && remainingDays >= 1)
			return <FifteenDaysWarning remainingDays={remainingDays} remainingSeconds={remainingSeconds} onAccept={close} certificateType={certificateType} />;
		if (remainingHours <= 24 && remainingHours > 0)
			return <HoursWarning remainingHours={remainingHours} remainingSeconds={remainingSeconds} onAccept={close} certificateType={certificateType} />;
		return <ExpiredWarning remainingSeconds={remainingSeconds} onAccept={close} certificateType={certificateType} />;
	};

	return (
		<DialogBody>
			<Notification />
		</DialogBody>
	);
};

export function init(config: GetDeviceConfigurationResponse) {
	status = config.FiscalSystem === FiscalSystem.None
		? null
		: {
			expirationDate: config.FiscalSystemCertificateExpirationdate!,
			remainingDays: 0,
			remainingHours: 0,
			lastNotification: null,
			fiscalSystem: config.FiscalSystem
		};

}

export function getNextNotificationStatus(currentStatus: Status, now: Date): Status {

	const remainingDays = differenceInDays(currentStatus.expirationDate, now);
	const remainingHours = differenceInHours(currentStatus.expirationDate, now);
	let lastNotification = null;

	// Si quedan mas de 60 días nunca voy a mostrar nada, por tanto no hay última notificación
	if (remainingDays > 60)
		lastNotification = null;
	// Si estoy entre 30 y y 60 días lo muestro sólo cuando no lo he mostrado ninguna vez
	else if (remainingDays > 30 && remainingDays <= 60)
		lastNotification = currentStatus.lastNotification !== null && differenceInHours(now, currentStatus.lastNotification!) >= HOURS_BETWEEN_NOTIFICATIONS_FOR_LESS_THAN_60_DAYS
			? now
			: currentStatus.lastNotification ?? now;
	// Si quedan menos de 30 siempre lo voy a sacar si ha pasado más de 1h
	else if (remainingDays <= 30)
		lastNotification = currentStatus.lastNotification !== null && differenceInHours(now, currentStatus.lastNotification!) >= HOURS_BETWEEN_NOTIFICATIONS_FOR_LESS_THAN_30_DAYS
			? now
			: currentStatus.lastNotification ?? now;

	return {
		...currentStatus,
		remainingDays,
		remainingHours,
		lastNotification,
	};
}

export function shouldShowNotification(currentStatus: Exclude<Status, { fiscalSystem: FiscalSystem.None }>, now: Date): boolean {

	const nextStatus = getNextNotificationStatus(currentStatus, now);

	if (nextStatus.remainingDays <= 0 && nextStatus.remainingHours <= 0)
		return true;

	// Si quedan menos de 30 días, lo mostramos si no se había mostrado o ha pasado más de una hora
	if (nextStatus.remainingDays <= 30)
		return currentStatus.lastNotification === null || differenceInHours(nextStatus.lastNotification!, currentStatus.lastNotification!) >= HOURS_BETWEEN_NOTIFICATIONS_FOR_LESS_THAN_30_DAYS;

	// Si quedan menos de 60 días lo mostramos sólo si no se ha mostrado o han pasado 24 horas
	if (nextStatus.remainingDays <= 60)
		return currentStatus.lastNotification === null || differenceInHours(nextStatus.lastNotification!, currentStatus.lastNotification!) >= HOURS_BETWEEN_NOTIFICATIONS_FOR_LESS_THAN_60_DAYS;

	return false;
}

export async function notifyExpirationIfNeeded() {

	if (status === null)
		return;

	const now = new Date();
	if (shouldShowNotification(status, now)) {
		status = getNextNotificationStatus(status, now);
		await Modal.show(NotificationModal, { ...status, placement: 'top', size: 'sm' });
	}
}

